import { LogData } from '../models/generic-log.model';
import { Pageable } from '../../../core/models/pageable.model';

/**
 * Campos de auditoria que identificam uma entrada de log.
 * Qualquer objeto que possua essas propriedades pode ser convertido.
 */
export interface LogEntry {
    createdBy: string;
    createdAt: Date | string;
    [key: string]: unknown;
}

/**
 * Converte uma lista paginada de objetos (`Pageable<T>`) para o formato
 * `Pageable<LogData>` consumido pelo `generic-logs`.
 *
 * A lógica de diff "de → para" é construída comparando cada item com o
 * item imediatamente seguinte na lista. Assumindo que a API retorna os
 * itens em ordem decrescente (mais recente primeiro), o item `[i+1]`
 * representa o estado anterior (from) e o item `[i]` o estado atual (to).
 *
 * Para o último item da página (o mais antigo dela), o `from` normalmente
 * ficaria vazio — pois o predecessor está na próxima página. Para cobrir
 * esse caso, passe o primeiro item da próxima página via `nextPageFirstItem`.
 *
 * @param page              Página paginada de objetos a converter.
 * @param nextPageFirstItem Primeiro item da próxima página (mais antigo que
 *                          todos os itens da página atual). Usado como `from`
 *                          do último item da página. Opcional.
 * @param auditKeys         Chaves que NÃO devem aparecer no diff (são usadas
 *                          apenas como metadados do log).
 *                          Por padrão: `createdBy` e `createdAt`.
 */
export function convertPageToLogs<T extends LogEntry>(
    page: Pageable<T>,
    nextPageFirstItem?: T | null,
    auditKeys: (keyof T & string)[] = ['createdBy', 'createdAt']
): Pageable<LogData> {
    const auditSet = new Set<string>(auditKeys);

    const extractPayload = (item: T): Record<string, unknown> =>
        Object.entries(item)
            .filter(([key]) => !auditSet.has(key))
            .reduce<Record<string, unknown>>((acc, [key, value]) => {
                acc[key] = value;
                return acc;
            }, {});

    const toDateString = (value: Date | string): string =>
        value instanceof Date ? value.toISOString() : String(value);

    const lastIndex = page.items.length - 1;

    const logItems: LogData[] = page.items.map((item, index) => {
        const isLast = index === lastIndex;
        const previous = isLast
            ? (nextPageFirstItem ?? null)
            : page.items[index + 1];

        return {
            from: previous ? extractPayload(previous) : {},
            to: extractPayload(item),
            createdAt: toDateString(item.createdAt),
            createdBy: item.createdBy,
        };
    });

    return { ...page, items: logItems };
}
